import { useEffect, useState } from "react";
import { Loader2, Eye, MapPin, Save, Home, Globe2, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/api";

/**
 * V25 — Tutor Enquiry Visibility settings card.
 * Lets admin choose globally what enquiry types tutors can see (Home / Online / Both)
 * and an optional default Home-Tuition city filter (also reused as the admin
 * dropdown default). All changes propagate instantly via the public
 * /tutor/portal/status polling on the Tutor SPA.
 */
const MODES = [
    { value: "home", label: "Home Tuition Only", icon: Home, hint: "Tutors see ONLY home-tuition enquiries." },
    { value: "online", label: "Online Tuition Only", icon: Globe2, hint: "Tutors see ONLY online enquiries." },
    { value: "both", label: "Home + Online (Both)", icon: Layers, hint: "Tutors see both modes (default)." },
];

export default function TutorEnquiryVisibilitySettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("both");
    const [cityFilter, setCityFilter] = useState("all");
    const [availableCities, setAvailableCities] = useState([]);
    const [meta, setMeta] = useState({ mode: null, city: null });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/tutor-enquiry-visibility/settings");
            setMode(data?.mode || "both");
            setCityFilter(data?.home_city_filter || "all");
            setAvailableCities(Array.isArray(data?.available_cities) ? data.available_cities : []);
            setMeta({
                mode: data?.mode_setting || null,
                city: data?.home_city_filter_setting || null,
            });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/admin/tutor-enquiry-visibility/settings", {
                mode,
                home_city_filter: cityFilter,
            });
            toast.success("Tutor enquiry visibility settings updated successfully.");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4" data-testid="tutor-enquiry-visibility">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2">
                        <Eye className="w-5 h-5 text-[#1E3A8A]" /> Tutor Enquiry Visibility
                    </h2>
                    <p className="text-sm text-slate-600 mt-1 max-w-xl">
                        Choose what enquiry types tutors can see. Backend-enforced — tutors cannot bypass this via URL or browser inspect. Admins always see every enquiry.
                    </p>
                </div>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            </header>

            <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" data-testid="enquiry-mode-radios">
                {MODES.map((opt) => {
                    const active = mode === opt.value;
                    const Icon = opt.icon;
                    return (
                        <label
                            key={opt.value}
                            data-testid={`enquiry-mode-${opt.value}`}
                            className={`relative flex flex-col gap-1 p-3 rounded-xl border-2 cursor-pointer transition
                                ${active
                                    ? "border-[#1E3A8A] bg-blue-50 shadow-sm"
                                    : "border-slate-200 hover:border-slate-300 bg-white"}`}
                        >
                            <input
                                type="radio"
                                className="sr-only"
                                name="enquiry-mode"
                                value={opt.value}
                                checked={active}
                                onChange={() => setMode(opt.value)}
                                data-testid={`enquiry-mode-${opt.value}-input`}
                            />
                            <span className="inline-flex items-center gap-2 font-semibold text-sm text-[#0F172A]">
                                <Icon className={`w-4 h-4 ${active ? "text-[#1E3A8A]" : "text-slate-400"}`} />
                                {opt.label}
                            </span>
                            <span className="text-[11px] text-slate-600 leading-snug">{opt.hint}</span>
                        </label>
                    );
                })}
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-2">
                <div className="font-semibold text-sm text-[#0F172A] inline-flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-700" /> Home Tuition City Filter (admin default)
                </div>
                <p className="text-[11px] text-slate-500 -mt-1 max-w-xl">
                    Default city filter for the Admin → Demo Enquiries view. Choose <em>All Cities</em> to see everything by default. Online enquiries are not affected.
                </p>
                <select
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    data-testid="home-city-filter-select"
                    className="w-full sm:w-72 px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                >
                    <option value="all">All Cities</option>
                    {availableCities.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
                <div className="text-[11px] text-slate-500" data-testid="enquiry-visibility-meta">
                    {meta.mode?.updated_at && <>Mode last set {new Date(meta.mode.updated_at).toLocaleString()} by {meta.mode.updated_by}. </>}
                    {meta.city?.updated_at && <>City filter last set {new Date(meta.city.updated_at).toLocaleString()} by {meta.city.updated_by}.</>}
                </div>
                <button
                    type="button"
                    onClick={save}
                    disabled={loading || saving}
                    data-testid="enquiry-visibility-save"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm font-semibold disabled:opacity-60"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Settings
                </button>
            </div>
        </section>
    );
}
